import React from 'react';

import Button from '@hero/ui-kit/inputs/Button';
import CommentIcon from '../../../../../icons/CommentIcon';
import CopyIcon from '../../../../../icons/CopyIcon';
import ThumbDownIcon from '../../../../../icons/ThumbDownIcon';
import ThumbUpIcon from '../../../../../icons/ThumbUpIcon';
import useSubmitAIInsightFeedback from '../../../api/useSubmitAIInsightFeedback';
import * as Style from './style.module.scss';

type Rating = 'up' | 'down';

// Builds the clipboard text: a dated intro sentence followed by the body found
// between the first and second `## ` markdown titles (the first section's content).
const buildCopyContent = (text: string): string => {
    const monthYear = new Intl.DateTimeFormat('en-US', {
        month: '2-digit',
        year: 'numeric'
    }).format(new Date());
    const intro = `After reviewing patient data for the month of ${monthYear}, the following observations were noted:`;

    const firstTitleIdx = text.indexOf('## ');
    if (firstTitleIdx === -1) return `${intro}\n\n${text.trim()}`;

    const bodyStart = text.indexOf('\n', firstTitleIdx);
    const start = bodyStart === -1 ? text.length : bodyStart + 1;
    const secondTitleIdx = text.indexOf('## ', start);
    const body = (
        secondTitleIdx === -1 ? text.slice(start) : text.slice(start, secondTitleIdx)
    ).trim();

    return `${intro}\n${body}`;
};

interface InsightFeedbackProps {
    callId: string;
    copyText?: string;
}

const InsightFeedback: React.FC<InsightFeedbackProps> = ({ callId, copyText }) => {
    const [rating, setRating] = React.useState<Rating | null>(null);
    const [commentDraft, setCommentDraft] = React.useState<string | null>(null);
    const [copied, setCopied] = React.useState(false);

    React.useEffect(() => {
        if (!copied) return undefined;
        const timer = window.setTimeout(() => setCopied(false), 2000);
        return () => window.clearTimeout(timer);
    }, [copied]);

    const handleCopy = async () => {
        if (!copyText) return;
        try {
            await navigator.clipboard.writeText(buildCopyContent(copyText));
            setCopied(true);
        } catch {
            // Clipboard write can reject under iframe / insecure-context;
            // fall through silently so the feedback bar stays usable.
        }
    };

    const { mutate } = useSubmitAIInsightFeedback();

    const commentSent = commentDraft === 'sent';
    const showComment = commentDraft !== null && !commentSent;

    const handleThumbClick = (value: Rating) => {
        const newRating = rating === value ? null : value;
        setRating(newRating);

        if (newRating) {
            mutate({
                payload: {
                    call_id: callId,
                    agent_type: 'care_manager_digest',
                    rating: newRating
                }
            });
        }
    };

    const handleSubmitComment = () => {
        if (!commentDraft?.trim()) return;

        mutate(
            {
                payload: {
                    call_id: callId,
                    agent_type: 'care_manager_digest',
                    comment: commentDraft
                }
            },
            {
                onSuccess: () => {
                    setCommentDraft('sent');
                }
            }
        );
    };

    return (
        <div className={Style.wrapper}>
            <div className={Style.group}>
                {copyText ? (
                    <button
                        type="button"
                        className={copied ? Style.copyButtonActive : Style.copyButton}
                        onClick={handleCopy}
                        aria-label="Copy AI digest to clipboard"
                    >
                        <CopyIcon size="small" />
                        <span>{copied ? 'Copied' : 'Copy'}</span>
                    </button>
                ) : null}
                <span className={Style.label}>Was this insight helpful?</span>
                <button
                    type="button"
                    className={rating === 'up' ? Style.segmentUpActive : Style.segmentUp}
                    onClick={() => handleThumbClick('up')}
                >
                    <ThumbUpIcon size="small" />
                </button>
                <button
                    type="button"
                    className={rating === 'down' ? Style.segmentActive : Style.segment}
                    onClick={() => handleThumbClick('down')}
                >
                    <ThumbDownIcon size="small" />
                </button>
                {commentSent ? (
                    <span className={Style.segmentSuccess}>
                        <CommentIcon size="small" />
                        <span>Comment sent</span>
                    </span>
                ) : (
                    <button
                        type="button"
                        className={showComment ? Style.segmentActive : Style.segment}
                        onClick={() => setCommentDraft(showComment ? null : '')}
                    >
                        <CommentIcon size="small" />
                        <span>Add comment</span>
                    </button>
                )}
            </div>

            {showComment && (
                <div className={Style.commentSection}>
                    <textarea
                        autoFocus
                        className={Style.textarea}
                        placeholder="Share your thoughts on this insight..."
                        value={commentDraft}
                        onChange={(e) => setCommentDraft(e.target.value)}
                        rows={3}
                    />
                    <div className={Style.actions}>
                        <Button
                            variant="outlineSecondary"
                            size="small"
                            onClick={() => setCommentDraft(null)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            size="small"
                            onClick={handleSubmitComment}
                            disabled={!commentDraft?.trim()}
                        >
                            Send
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InsightFeedback;
